import { defineStore } from "pinia"
import { ProfilesApi } from "@/services/profiles-service"
import { useSystemFeedback } from "./systemFeedback"
import router from "@/router"
import type {
  ProfileId,
  Profile,
  ProfileRequest,
  ApprovalRequest,
  UserGroupExists
} from "@/types/profile"
import type { ApiError } from "@/types/api"

enum profileStatus {
  "waiting" = 1,
  "approved" = 2,
  "denied" = 3,
  "cancelled" = 4
}

export const useProfiles = defineStore("profile", {
  state: () => {
    return {
      listProfiles: [] as Profile[],
      filteredProfiles: [] as Profile[],
      listGroupsProfiles: [] as UserGroupExists[],
      profile: {} as Profile,
      api: new ProfilesApi()
    }
  },

  getters: {
    profileStatus: state => profileStatus[state.profile.status as profileStatus]
  },

  actions: {
    async getProfiles() {
      this.listProfiles = []
      this.filteredProfiles = []
      try {
        const response = await this.api.getProfiles()
        this.listProfiles = response.data
        this.filteredProfiles = response.data
        return response
      } catch (error) {
        this.handleErrors(error as ApiError)
      }
    },

    async getProfile(id: ProfileId) {
      this.profile = {} as Profile
      try {
        const response = await this.api.getProfile(id)
        const status = profileStatus[response.data.status as profileStatus]
        if (status === "cancelled") {
          return this.handleErrors({
            response: { data: { detail: "api016" } }
          } as ApiError)
        }
        this.profile = response.data
        return response
      } catch (error) {
        this.handleErrors(error as ApiError)
      }
    },

    async getGroups() {
      this.listGroupsProfiles = []
      try {
        const response = await this.api.getGroups()
        this.listGroupsProfiles = response.data
        return response
      } catch (error) {
        this.handleErrors(error as ApiError)
      }
    },

    async requestProfile(data: ProfileRequest) {
      try {
        return await this.api.requestProfile(data)
      } catch (error) {
        this.handleErrors(error as ApiError)
      }
    },

    async handleProfileApproval(data: ApprovalRequest) {
      try {
        return await this.api.handleProfileApproval(data)
      } catch (error) {
        this.handleErrors(error as ApiError)
      }
    },

    async cancelRequestProfile(id: ProfileId) {
      try {
        await this.api.cancelRequestProfile(id)
        router.push({ name: "userProfiles" })
      } catch (error) {
        this.handleErrors(error as ApiError)
      }
    },

    async getPermissions() {
      try {
        return await this.api.getPermissions()
      } catch (error) {
        this.handleErrors(error as ApiError)
      }
    },

    handleErrors(error: ApiError) {
      const errorMessage = error?.response
        ? `backend.${error.response.data.detail}`
        : "requestError.default"
      useSystemFeedback().setFeedback({ type: "danger", text: errorMessage })
    }
  }
})
